import CardBase from "@/components/cards/CardBase";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconProvider } from "@/components/ui";
import Step0 from "@/components/finishProfile_Parts/Step0";
import Step1 from "@/components/finishProfile_Parts/Step1";
import Step2 from "@/components/finishProfile_Parts/Step2";
import Step3 from "@/components/finishProfile_Parts/Step3"; 
import { toast } from "@/hooks/use-toast";
import { useAuthStore } from "@/lib/api-functions/api-auth";
import apiService from "@/lib/api-functions/api-service";
import { supabase } from "@/lib/config/supabase";

export default function FinishProfilePage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [invitationCode, setInvitationCode] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isLoadingEmail, setIsLoadingEmail] = useState(true);
  const [step, setStep] = useState(0); // Start at step 0 (password) for invited users, 1 for regular
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalSteps, setTotalSteps] = useState(3); // Max step index: 3 for non-invited (steps 1,2,3), 2 for invited (steps 0,1,2)
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, signOut, fetchProfile } = useAuthStore();

  // Note: For invited users, no session exists yet - they'll create their account when setting password
  // Check if there's an existing session (for users who came from regular signup)
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          console.log('✅ Existing session found (regular signup user)');
          // Update auth store if possible
          try {
            await fetchProfile();
          } catch (error) {
            console.log('ℹ️ Could not fetch profile yet (expected for incomplete profiles)');
          }
        } else {
          console.log('ℹ️ No existing session (invited user - will create account when setting password)');
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    };
    
    checkExistingSession();
  }, [fetchProfile]);

  // Check for invitation code from URL or user metadata and get user email
  useEffect(() => {
    const checkInvitationCode = async () => {
      console.log('=== FinishProfilePage: Checking invitation and email ===');
      
      // PRIORITY 1: Check for email in URL params (most reliable for invited users)
      const emailFromUrl = searchParams.get('email');
      if (emailFromUrl) {
        console.log('✅ Email from URL (invited user):', emailFromUrl);
        setUserEmail(emailFromUrl);
        setIsLoadingEmail(false);
      }
      
      // Check for invitation code in URL params
      const codeFromUrl = searchParams.get('invitation_code');
      const passwordAlreadySet = searchParams.get('password_set') === 'true';
      
      if (codeFromUrl) {
        console.log('Invitation code from URL:', codeFromUrl);
        setInvitationCode(codeFromUrl);
        
        // Check if password was already set during manual signup
        if (passwordAlreadySet) {
          console.log('✅ Manual signup with invitation code - password already set, skipping Step 0');
          setStep(1); // Skip password step
          setTotalSteps(2); // Final step index: 2 (steps 1, 2)
        } else {
          console.log('✅ Magic link invitation - will set password in Step 0');
          setStep(0); // Start at password step for magic link invitations
          setTotalSteps(2); // Final step index: 2 (steps 0, 1, 2)
        }
      } else {
        console.log('ℹ️ No invitation code - user came from regular signup');
        console.log('✅ Skipping password step (already set during signup)');
        setStep(1); // Skip password step for regular signup users
        setTotalSteps(3); // Final step index: 3 (steps 1, 2, 3)
      }

      // If email already found in URL, we're done
      if (emailFromUrl) {
        console.log('=== End FinishProfilePage check (email from URL) ===');
        return;
      }

      // PRIORITY 2: Get email from Supabase session
      let emailFound = false;
      let detectedEmail = '';
      
      try {
        const { data: { user: supabaseUser } } = await supabase.auth.getUser();
        console.log('Supabase user data:', supabaseUser);
        
        if (supabaseUser?.email) {
          console.log('Setting user email from Supabase session:', supabaseUser.email);
          setUserEmail(supabaseUser.email);
          detectedEmail = supabaseUser.email;
          emailFound = true;
          
          // Check for invitation code in metadata if not in URL
          if (!codeFromUrl && supabaseUser.user_metadata?.invitation_code) {
            console.log('Invitation code from user metadata:', supabaseUser.user_metadata.invitation_code);
            setInvitationCode(supabaseUser.user_metadata.invitation_code);
            
            // Check if password was already set during manual signup
            // If password_set flag is not in URL but we have invitation code in metadata,
            // it's likely a magic link invitation (password needs to be set)
            if (passwordAlreadySet) {
              console.log('✅ Manual signup with invitation code - password already set, skipping Step 0');
              setStep(1); // Skip password step
              setTotalSteps(2); // Final step index: 2 (steps 1, 2)
            } else {
              console.log('✅ Magic link invitation - will set password in Step 0');
              setStep(0); // Start at password step for magic link invitations
              setTotalSteps(2); // Final step index: 2 (steps 0, 1, 2)
            }
          } else if (!codeFromUrl) {
            console.log('ℹ️ No invitation code found - user came from regular signup');
            console.log('✅ Skipping password step (already set during signup)');
            setStep(1); // Skip password step for regular signup users
            setTotalSteps(3); // Final step index: 3 (steps 1, 2, 3)
          }
        }
      } catch (error) {
        console.error('Error checking user metadata:', error);
      }
      
      // PRIORITY 3: Fallback to auth store
      if (!emailFound && user?.email) {
        console.log('Setting user email from auth store (fallback):', user.email);
        setUserEmail(user.email);
        detectedEmail = user.email;
        emailFound = true;
      }
      
      if (!emailFound) {
        console.warn('⚠️ No email found from any source');
      }
      
      console.log('Final userEmail:', detectedEmail || 'none');
      console.log('=== End FinishProfilePage check ===');
      
      // Mark email loading as complete
      setIsLoadingEmail(false);
    };

    checkInvitationCode();
  }, [searchParams, user]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
      toast({
        title: "Logout Failed",
        description: "An error occurred while logging out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleNext = async () => {
    // Validation for each step
    if (step === 0) {
      // Step 0: Password validation and setting
      if (!password.trim()) {
        toast({
          title: "Required Field",
          description: "Please enter a password.",
          variant: "destructive",
        });
        return;
      }
      
      if (password.length < 6) {
        toast({
          title: "Password Too Short",
          description: "Password must be at least 6 characters long.",
          variant: "destructive",
        });
        return;
      }
      
      if (password !== confirmPassword) {
        toast({
          title: "Passwords Don't Match",
          description: "Please make sure both passwords match.",
          variant: "destructive",
        });
        return;
      }
      
      // For invited users, we'll create the account on the backend during onboarding
      // Just validate and store the password for now
      console.log('✅ Password validated for invited user');
      
      toast({
        title: "Password Set",
        description: "Let's complete your profile.",
      });
      setStep(1); // Move to profile info
    } else if (step === 1) {
      // Step 1: Profile info validation
      if (!firstName.trim() || !lastName.trim()) {
        toast({
          title: "Required Fields",
          description: "Please fill in both first name and last name.",
          variant: "destructive",
        });
        return;
      }
      
      // If invitation code exists, skip company selection
      if (invitationCode.trim()) {
        setStep(2); // Go to review
      } else {
        setStep(2); // Go to company selection
      }
    } else if (step === 2) {
      // Step 2: Company selection validation (or review if invited)
      if (!invitationCode.trim()) {
        // Validate company selection
        if (!companyName.trim()) {
          toast({
            title: "Required Field",
            description: "Please enter a company name.",
            variant: "destructive",
          });
          return;
        }
      }
      // Move to final review/completion
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: "Missing Information",
        description: "Please complete all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (!companyName.trim() && !invitationCode.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide either a company name or invitation code.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if this is an invited user who needs account creation
      const isInvitedUser = invitationCode.trim() && password;
      
      // Get the current Supabase session token (for regular signup users)
      const { data: sessionData } = await supabase.auth.getSession();
      const supabaseToken = sessionData.session?.access_token;
      
      // For invited users, we use a temporary token approach
      // For regular users, use their existing session
      if (supabaseToken) {
        console.log('📝 Using existing Supabase token for onboarding API call');
        apiService.storeToken(supabaseToken);
      } else if (isInvitedUser) {
        console.log('📝 Invited user - backend will create account and return session');
        // Use a dummy token for the API call - backend will handle authentication differently
        apiService.storeToken('pending-user-creation');
      } else {
        console.warn('⚠️ No Supabase token found - this may cause the API call to fail');
      }
      
      const requestData: {
        first_name: string;
        last_name: string;
        username?: string;
        company_name?: string;
        invitation_code?: string;
        password?: string;
        email?: string;
      } = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      };

      // Add username if provided
      if (username.trim()) {
        requestData.username = username.trim();
      }

      if (invitationCode.trim()) {
        requestData.invitation_code = invitationCode.trim();
        
        // For invited users, include password and email so backend can create account
        if (password) {
          requestData.password = password;
          requestData.email = userEmail;
        }
      } else {
        requestData.company_name = companyName.trim();
      }

      const response = await apiService.completeOnboarding(requestData);

      if (response.success) {
        console.log('✅ Onboarding completed successfully');
        
        // If backend created the user and returned a Supabase session, set it
        if (response.data?.supabase_session) {
          console.log('🔐 Setting Supabase session from backend');
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: response.data.supabase_session.access_token,
            refresh_token: response.data.supabase_session.refresh_token
          });
          
          if (sessionError) {
            console.error('Error setting Supabase session:', sessionError);
          } else {
            console.log('✅ Supabase session established');
          }
        }
        
        // The API returns an API JWT token - store it
        if (response.data?.token) {
          console.log('🔐 Storing API JWT token from onboarding response');
          apiService.storeToken(response.data.token);
        } else {
          console.warn('⚠️ No API token returned from onboarding - user may need to log in again');
        }
        
        // Update auth store with new profile data
        const { fetchProfile } = useAuthStore.getState();
        await fetchProfile();
        
        toast({
          title: "Welcome to LocaNest!",
          description: invitationCode.trim() 
            ? "You have successfully joined your team!" 
            : "Your account has been set up successfully.",
        });
        
        // Navigate to platform root - this will trigger SimplifiedUserDataProvider to load companies/games
        navigate("/home");
      } else {
        toast({
          title: "Setup Failed",
          description: response.error || "Please try again later.",
          variant: "destructive",
        });
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error completing onboarding:", error);
      toast({
        title: "Setup Failed",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background:
          "linear-gradient(116deg, var(--Purple-1, #6E35FF) 0%, #30E38D 54.16%)",
      }}
    >
      <div className="w-full max-w-2xl mx-4">
        <CardBase className="p-12" shadowType="black">
          {/* Logo and Logout */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex-1"></div>
            <img src="/LocaNest_Purple.svg" alt="LocaNest" className="h-12" />
            <div className="flex-1 flex justify-end">
              <button
                onClick={handleLogout}
                disabled={isSubmitting}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Header with back button */}
          <div className="flex flex-row items-center justify-left gap-2 w-full mb-2">
            <button
              onClick={handleBack}
              disabled={step === 0 || isSubmitting}
              className={`w-6 h-6 ${
                step === 0 || isSubmitting
                  ? "invisible"
                  : "cursor-pointer text-gray-600 hover:text-gray-900"
              }`}
            >
              <IconProvider iconstring="fa-chevron-left" className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-gray-900">
              Complete Your Setup
            </h2>
          </div>

          {/* Email display */}
          {isLoadingEmail ? (
            <div className="mb-6 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg animate-pulse">
              <p className="text-sm text-gray-400">{'Loading account info...'}</p>
              <div className="h-5 bg-gray-200 rounded w-48 mt-1"></div>
            </div>
          ) : userEmail ? (
            <div className="mb-6 px-4 py-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-gray-600">
                Setting up account for:
              </p>
              <p className="text-base font-semibold text-purple-700">
                {userEmail}
              </p>
            </div>
          ) : (
            <div className="mb-6 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ Email not detected. Check browser console for details.
              </p>
            </div>
          )}

          {/* Progress indicator */}
          <div className="flex flex-row gap-2 w-full mb-8">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className={`h-2 w-full rounded-full transition-colors ${
                  step === index
                    ? "bg-purple-1"
                    : step > index
                    ? "bg-purple-300"
                    : "bg-gray-300"
                }`}
              />
            ))}
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {step === 0 && (
              <Step0
                password={password}
                setPassword={setPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
              />
            )}
            {step === 1 && (
              <Step1
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                username={username}
                setUsername={setUsername}
              />
            )}
            {step === 2 && !invitationCode && (
              <Step2
                companyName={companyName}
                setCompanyName={setCompanyName}
                invitationCode={invitationCode}
                setInvitationCode={setInvitationCode}
              />
            )}
            {((step === 2 && invitationCode) || step === 3) && (
              <Step3 companyName={companyName} invitationCode={invitationCode} />
            )}
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3">
            {step < totalSteps ? (
              <button
                onClick={handleNext}
                disabled={isSubmitting}
                className="px-6 py-2 bg-brand-brand_purple text-white font-medium rounded-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-brand-brand_purple focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (step === 0 ? 'Creating Account...' : 'Processing...') : 'Next'}
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={isSubmitting}
                className="px-6 py-2 bg-brand-brand_purple text-white font-medium rounded-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-brand-brand_purple focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? "Setting up..." : "Complete Setup"}
              </button>
            )}
          </div>
        </CardBase>
      </div>
    </div>
  );
}

